import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import { db } from "../../lib/firebase";
import { useAuth } from "../../lib/auth";
import { useTheme } from "../../lib/theme";
import {
  Menu, Bell, Sun, Moon, ChevronRight, ChevronDown, User, LogOut, Settings, Store, Check,
} from "lucide-react";
import { clsx } from "clsx";

// Map route paths to breadcrumb labels
const breadcrumbMap = {
  "/": "Dashboard",
  "/orders": "Orders",
  "/orders/placed": "Placed Orders",
  "/orders/accepted": "Accepted Orders",
  "/products": "Products",
  "/products/create": "New Product",
  "/categories": "Categories",
  "/categories/create": "New Category",
  "/coupons": "Coupons",
  "/coupons/create": "New Coupon",
  "/special-offers": "Special Offers",
  "/reviews": "Customer Reviews",
  "/payments/earnings": "Earnings",
  "/payments/payouts": "Payouts",
  "/payments/request": "Request Payout",
  "/dinein": "Dine-In Bookings",
  "/settings/store": "Store Settings",
  "/settings/profile": "Profile",
  "/drivers": "Drivers",
};

function getBreadcrumbs(pathname) {
  // Direct match
  if (breadcrumbMap[pathname]) {
    return [{ label: breadcrumbMap[pathname], path: pathname }];
  }

  // Build from segments
  const segments = pathname.split("/").filter(Boolean);
  const crumbs = [];
  let current = "";

  for (let i = 0; i < segments.length; i++) {
    current += "/" + segments[i];
    const label = breadcrumbMap[current];
    if (label) {
      crumbs.push({ label, path: current });
    } else {
      // Dynamic segment (ID or action like "edit")
      const seg = segments[i];
      if (seg === "edit") {
        crumbs.push({ label: "Edit", path: current });
      } else if (seg === "create") {
        crumbs.push({ label: "Create", path: current });
      } else if (seg === "print") {
        crumbs.push({ label: "Print", path: current });
      } else {
        // Likely an ID - show as detail
        crumbs.push({ label: "Detail", path: current });
      }
    }
  }

  return crumbs.length > 0 ? crumbs : [{ label: "Dashboard", path: "/" }];
}

export default function Topbar({ onMenuToggle, sidebarCollapsed }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { vendor, vendorName, logout, vendorUser, allVendors, switchVendor, pendingOrderCount: authPendingCount } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [pendingCount, setPendingCount] = useState(0);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [storeSwitchOpen, setStoreSwitchOpen] = useState(false);
  const storeSwitchRef = useRef(null);
  const multiStore = Array.isArray(allVendors) && allVendors.length > 1;

  const isDark = theme === "dark";
  const displayName = vendorName || vendor?.title || vendor?.name || "Store";
  const vendorPhoto = vendor?.photo || vendor?.profilePictureURL || null;
  const userPhoto = vendorUser?.profilePictureURL || vendorPhoto;

  // GHOST-FIX-STORE-WEB #25: pending-orders count now sourced from a
  // shared listener in AuthContext — Sidebar + Topbar both read the
  // same number and we don't pay two Firestore snapshots for it.
  useEffect(() => {
    setPendingCount(authPendingCount || 0);
  }, [authPendingCount]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
      if (storeSwitchRef.current && !storeSwitchRef.current.contains(e.target)) {
        setStoreSwitchOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const breadcrumbs = getBreadcrumbs(location.pathname);

  const handleLogout = async () => {
    setDropdownOpen(false);
    try {
      await logout();
      navigate("/login", { replace: true });
    } catch (err) {
      console.error("Logout error:", err);
    }
  };

  return (
    <header
      className={clsx(
        "sticky top-0 z-30 h-16 flex items-center justify-between gap-4 px-4 sm:px-6 border-b transition-colors",
        isDark
          ? "bg-gray-900/95 border-white/10 backdrop-blur-md"
          : "bg-white/95 border-gray-200 backdrop-blur-md"
      )}
    >
      {/* Left: Menu toggle + Breadcrumb */}
      <div className="flex items-center gap-3 min-w-0">
        <button
          onClick={onMenuToggle}
          className={clsx(
            "lg:hidden p-2 rounded-lg transition-colors",
            isDark
              ? "text-white/60 hover:bg-white/10 hover:text-white"
              : "text-gray-500 hover:bg-gray-100 hover:text-gray-700"
          )}
          aria-label="Toggle menu"
        >
          <Menu size={20} />
        </button>

        {/* Breadcrumb */}
        <nav className="flex items-center gap-1.5 text-sm min-w-0">
          {breadcrumbs.map((crumb, idx) => (
            <div key={crumb.path} className="flex items-center gap-1.5 min-w-0">
              {idx > 0 && (
                <ChevronRight
                  size={12}
                  className={isDark ? "text-white/30" : "text-gray-300"}
                />
              )}
              {idx === breadcrumbs.length - 1 ? (
                <span
                  className={clsx(
                    "font-semibold truncate",
                    isDark ? "text-white" : "text-gray-900"
                  )}
                >
                  {crumb.label}
                </span>
              ) : (
                <Link
                  to={crumb.path}
                  className={clsx(
                    "truncate transition-colors",
                    isDark
                      ? "text-white/50 hover:text-white/80"
                      : "text-gray-400 hover:text-gray-600"
                  )}
                >
                  {crumb.label}
                </Link>
              )}
            </div>
          ))}
        </nav>
      </div>

      {/* Right: Actions */}
      <div className="flex items-center gap-2">
        {/* Store name / multi-store switcher */}
        {multiStore ? (
          <div className="relative hidden md:block" ref={storeSwitchRef}>
            <button
              onClick={() => setStoreSwitchOpen((p) => !p)}
              className={clsx(
                "flex items-center gap-2 text-xs font-medium px-3 py-1.5 rounded-full transition-colors",
                isDark
                  ? "bg-white/5 text-white/70 hover:bg-white/10"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              )}
            >
              <Store size={13} />
              <span className="max-w-[160px] truncate">{displayName}</span>
              <ChevronDown size={13} />
            </button>
            {storeSwitchOpen && (
              <div
                className={clsx(
                  "absolute right-0 top-full mt-2 w-64 rounded-xl border shadow-xl overflow-hidden z-40",
                  isDark ? "bg-gray-800 border-white/10" : "bg-white border-gray-200"
                )}
              >
                <div className={clsx("px-3 py-2 text-[10px] uppercase tracking-wider", isDark ? "text-white/40" : "text-gray-400")}>
                  Switch store
                </div>
                <div className="max-h-72 overflow-y-auto py-1">
                  {allVendors.map((v) => {
                    const active = v.id === vendor?.id;
                    return (
                      <button
                        key={v.id}
                        onClick={() => { switchVendor(v.id); setStoreSwitchOpen(false); }}
                        className={clsx(
                          "flex items-center justify-between gap-2 w-full px-4 py-2.5 text-sm transition-colors",
                          active
                            ? (isDark ? "bg-white/5 text-amigo-orange" : "bg-amigo-orange/5 text-amigo-orange")
                            : (isDark ? "text-white/70 hover:bg-white/5" : "text-gray-600 hover:bg-gray-50")
                        )}
                      >
                        <span className="truncate">{v.title || v.name || v.id}</span>
                        {active && <Check size={15} />}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        ) : (
          <span
            className={clsx(
              "hidden md:block text-xs font-medium px-3 py-1.5 rounded-full",
              isDark ? "bg-white/5 text-white/50" : "bg-gray-100 text-gray-500"
            )}
          >
            {displayName}
          </span>
        )}

        {/* Theme toggle */}
        <button
          onClick={toggleTheme}
          className={clsx(
            "p-2 rounded-lg transition-colors",
            isDark
              ? "text-white/50 hover:bg-white/10 hover:text-amigo-orange"
              : "text-gray-400 hover:bg-gray-100 hover:text-amigo-orange"
          )}
          aria-label="Toggle theme"
        >
          {isDark ? <Sun size={18} /> : <Moon size={18} />}
        </button>

        {/* Notification bell */}
        <button
          onClick={() => navigate("/orders/placed")}
          className={clsx(
            "relative p-2 rounded-lg transition-colors",
            isDark
              ? "text-white/50 hover:bg-white/10 hover:text-white"
              : "text-gray-400 hover:bg-gray-100 hover:text-gray-700"
          )}
          aria-label="Notifications"
        >
          <Bell size={18} />
          {pendingCount > 0 && (
            <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 flex items-center justify-center bg-amigo-orange text-white text-[9px] font-bold rounded-full leading-none">
              {pendingCount > 99 ? "99+" : pendingCount}
            </span>
          )}
        </button>

        {/* User dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setDropdownOpen((p) => !p)}
            className={clsx(
              "flex items-center gap-2 p-1.5 rounded-lg transition-colors",
              isDark
                ? "hover:bg-white/10"
                : "hover:bg-gray-100"
            )}
          >
            {userPhoto ? (
              <img
                src={userPhoto}
                alt=""
                className="w-8 h-8 rounded-full object-cover ring-2 ring-amigo-orange/30"
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amigo-orange to-amigo-teal flex items-center justify-center text-white text-xs font-bold">
                {displayName[0]?.toUpperCase()}
              </div>
            )}
          </button>

          {dropdownOpen && (
            <div
              className={clsx(
                "absolute right-0 top-full mt-2 w-56 rounded-xl border shadow-xl overflow-hidden",
                isDark
                  ? "bg-gray-800 border-white/10"
                  : "bg-white border-gray-200"
              )}
            >
              {/* User info */}
              <div
                className={clsx(
                  "px-4 py-3 border-b",
                  isDark ? "border-white/10" : "border-gray-100"
                )}
              >
                <p
                  className={clsx(
                    "text-sm font-semibold truncate",
                    isDark ? "text-white" : "text-gray-900"
                  )}
                >
                  {displayName}
                </p>
                <p
                  className={clsx(
                    "text-xs truncate mt-0.5",
                    isDark ? "text-white/40" : "text-gray-400"
                  )}
                >
                  {vendorUser?.email || "Store Owner"}
                </p>
              </div>

              {/* Menu items */}
              <div className="py-1">
                <Link
                  to="/settings/profile"
                  onClick={() => setDropdownOpen(false)}
                  className={clsx(
                    "flex items-center gap-3 px-4 py-2.5 text-sm transition-colors",
                    isDark
                      ? "text-white/70 hover:bg-white/5 hover:text-white"
                      : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                  )}
                >
                  <User size={15} />
                  <span>Profile</span>
                </Link>
                <Link
                  to="/settings/store"
                  onClick={() => setDropdownOpen(false)}
                  className={clsx(
                    "flex items-center gap-3 px-4 py-2.5 text-sm transition-colors",
                    isDark
                      ? "text-white/70 hover:bg-white/5 hover:text-white"
                      : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                  )}
                >
                  <Settings size={15} />
                  <span>Store Settings</span>
                </Link>
              </div>

              {/* Logout */}
              <div
                className={clsx(
                  "border-t py-1",
                  isDark ? "border-white/10" : "border-gray-100"
                )}
              >
                <button
                  onClick={handleLogout}
                  className={clsx(
                    "flex items-center gap-3 w-full px-4 py-2.5 text-sm transition-colors",
                    isDark
                      ? "text-red-400 hover:bg-red-500/10"
                      : "text-red-500 hover:bg-red-50"
                  )}
                >
                  <LogOut size={15} />
                  <span>Logout</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
